import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@weezevent/nacre';
import { fetcher } from '@weezevent/weezjs-core';

import { useInterval } from '../../utils/hooks';
import { useExportNotifications } from './exportNotifications';

export class Notification {
    constructor(type, sub_type, context, task_uuid, custom_wording = '') {
        this.type = type;
        this.sub_type = sub_type;
        this.context = context;
        this.task_uuid = task_uuid;
        this.custom_wording = custom_wording;
    }

    fromString(str) {
        const obj = JSON.parse(str);
        this.type = obj.type;
        this.sub_type = obj.sub_type;
        this.context = obj.context;
        this.task_uuid = obj.task_uuid;
        this.custom_wording = obj.custom_wording;
    }
}

export const ContextNotifications = React.createContext(null);

const INTERVAL = 10000;

export default function NotificationsProvider({ children }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [delay, setDelay] = useState(null);

    const pendingRef = React.useRef([]);
    const setPendingNotifications = data => {
        pendingRef.current = data;
    };

    const { t } = useTranslation();
    const { addExportNotification } = useExportNotifications();

    const updateLocalFromPending = () => {
        const notifsToPush = pendingRef.current ? [...pendingRef.current] : null;
        if (!notifsToPush) return;
        const strLocalStorage = window.localStorage.getItem('weez_notifications');
        let currentNotifications = strLocalStorage?.length > 0 ? JSON.parse(strLocalStorage) : [];
        if (currentNotifications?.length === 0) currentNotifications = [];
        const mergedNotifications = currentNotifications?.concat(notifsToPush);
        window.localStorage.setItem('weez_notifications', JSON.stringify(mergedNotifications));
        setPendingNotifications([]);
    };

    const showToast = React.useCallback(
        (status, foundNotif, report) => {
            let wording = `ticket.manager.${foundNotif?.context}.action.${foundNotif?.sub_type}.toast.${status}`;
            const reportCount = status === 'success' ? report?.count_items_success : report?.count_items_error;
            if (foundNotif.custom_wording) wording = foundNotif.custom_wording[status];
            toast[status](t(wording, { count: reportCount, total: report?.count_items_total }));
        },
        [t]
    );

    const getStatusAllNotifications = () => {
        setIsProcessing(true);
        updateLocalFromPending();
        const strLocalStorage = window.localStorage.getItem('weez_notifications');
        const _notifications = strLocalStorage ? JSON.parse(strLocalStorage) : [];
        // if not waiting
        if (typeof _notifications !== 'string' && _notifications?.length > 0) {
            const uuids = _notifications?.map(n => n?.task_uuid);
            const allTaskIds = uuids.map(uuid => `task_id=${uuid}`).join('&');
            fetcher
                .get(`/ticket/async-tasks/actions?${allTaskIds}`)
                .then(response => {
                    response?.response?.results.forEach(result => {
                        if (['SUCCESS', 'ERROR'].includes(result.state)) {
                            const indexDone = _notifications.findIndex(n => n.task_uuid === result.task_id);
                            const foundNotif = indexDone !== -1 ? _notifications.splice(indexDone, 1)[0] : null;
                            showToast(result.state.toLowerCase(), foundNotif, result.report);
                        }
                    });
                    //remove in localstorage
                    window.localStorage.setItem('weez_notifications', JSON.stringify(_notifications));
                })
                .catch(e => {
                    console.error(`${e.name} StatusCode: ${e.code}`);
                    window.localStorage.setItem('weez_notifications', JSON.stringify([]));
                })
                .finally(() => {
                    setIsProcessing(false);
                });
        } else {
            setIsProcessing(false);
            setDelay(null);
        }
    };

    const displayNotifications = () => {
        if (!isProcessing) {
            getStatusAllNotifications();
        }
    };

    useInterval(displayNotifications, delay);

    const onWindowBlur = () => {
        updateLocalFromPending();
        setDelay(null);
    };

    const startNotificationLoop = () => {
        if (delay == null) {
            setDelay(INTERVAL);
            displayNotifications();
        }
    };

    const addNotification = notification => {
        const notificationsToProcess = [...pendingRef.current];
        notificationsToProcess.push(notification);
        setPendingNotifications(notificationsToProcess);
        startNotificationLoop();
    };

    useEffect(() => {
        window.addEventListener('focus', startNotificationLoop);
        window.addEventListener('blur', onWindowBlur);
        return () => {
            window.removeEventListener('focus', startNotificationLoop);
            window.removeEventListener('blur', onWindowBlur);
        };
    }, []);

    return (
        <ContextNotifications.Provider
            value={{
                addNotification,
                addExportNotification
            }}
        >
            {children}
        </ContextNotifications.Provider>
    );
}

export function triggerToast(mode, actionInstance, count, response, t, toast, addNotification, wording = null) {
    // use obj for wording to allow more specific keys than only success/error suffix
    if (typeof wording == 'string') {
        const wording_obj = ['success', 'error'].reduce((words, s) => {
            words[s] = wording + s;
            return words;
        }, {});
        wording = wording_obj;
    }

    if (actionInstance.async) {
        addNotification(new Notification('async_actions', actionInstance?.constructor?.NAME, mode, response?.response?.task_id, wording));
        return toast.info(
            actionInstance?.constructor?.POST_ACTION_TOAST
                ? actionInstance?.constructor?.POST_ACTION_TOAST(response)
                : t('ticket.manager.' + mode + '.action.generic.toast.async', { count: count })
        );
    } else {
        return toast.success(
            wording
                ? t(wording.success, { count: count })
                : actionInstance?.constructor?.POST_ACTION_TOAST(response) ||
                      t('ticket.manager.' + mode + '.action.' + actionInstance.constructor.NAME + '.toast.success', { count: count })
        );
    }
}

export function useNotifications() {
    const ctx = React.useContext(ContextNotifications);

    const addNotification = ctx?.addNotification;
    const addExportNotification = ctx?.addExportNotification;

    return { addNotification, addExportNotification };
}
